/**
 * 合体（マージ）判定と合体キュー（純粋ロジック / Phaser 非依存）。
 *
 * 衝突イベント内で直接オブジェクトを破棄すると物理エンジンが壊れるため、
 * 「合体要求」をキューへ積み、物理更新後に安全に処理する。
 *
 * 二重合体の防止:
 *   request() を通した時点で両者の isMerging を true にするので、
 *   同一フレーム内に同じ料理が別の要求へ含まれることはない。
 */

/** 合体判定に必要な最小の情報。FoodPiece がこれを実装する。 */
export interface MergeCandidate {
  readonly id: number;
  readonly level: number;
  isMerging: boolean;
  /** 破棄済みなら false。 */
  readonly alive: boolean;
  readonly x: number;
  readonly y: number;
}

export type MergeDecisionType = 'merge' | 'max-level-bonus' | 'reject';

export type MergeRejectReason =
  'same-piece' | 'not-alive' | 'already-merging' | 'level-mismatch' | 'bonus-cooldown';

export interface MergeDecision {
  type: MergeDecisionType;
  /** type === 'merge' のときの生成レベル。 */
  nextLevel?: number;
  reason?: MergeRejectReason;
}

export interface MergeRequest {
  a: MergeCandidate;
  b: MergeCandidate;
  /** 生成レベル（max-level-bonus の場合は最高レベルのまま）。 */
  nextLevel: number;
  type: 'merge' | 'max-level-bonus';
  /** 生成位置（2つの中間地点）。 */
  x: number;
  y: number;
  requestedAt: number;
}

export interface MergeSystemOptions {
  /** ステージの最大レベル。 */
  maxLevel: number;
  /** 最高レベル同士のボーナス再加算を防ぐクールダウン(ms)。 */
  bonusCooldownMs: number;
  /** 現在時刻を返す関数（テスト用に差し替え可能）。 */
  now?: () => number;
}

/**
 * 2つの候補が合体できるかを判定する純粋関数。
 * 状態を一切変更しない。
 */
export function evaluateMerge(
  a: MergeCandidate,
  b: MergeCandidate,
  maxLevel: number,
): MergeDecision {
  if (a.id === b.id) return { type: 'reject', reason: 'same-piece' };
  if (!a.alive || !b.alive) return { type: 'reject', reason: 'not-alive' };
  if (a.isMerging || b.isMerging) return { type: 'reject', reason: 'already-merging' };
  if (a.level !== b.level) return { type: 'reject', reason: 'level-mismatch' };
  if (a.level >= maxLevel) return { type: 'max-level-bonus', nextLevel: maxLevel };
  return { type: 'merge', nextLevel: a.level + 1 };
}

export class MergeSystem {
  private readonly queue: MergeRequest[] = [];
  private readonly bonusCooldown = new Map<string, number>();
  private readonly now: () => number;
  private readonly maxLevel: number;
  private readonly bonusCooldownMs: number;

  constructor(options: MergeSystemOptions) {
    this.maxLevel = options.maxLevel;
    this.bonusCooldownMs = options.bonusCooldownMs;
    this.now = options.now ?? (() => Date.now());
  }

  get pendingCount(): number {
    return this.queue.length;
  }

  /**
   * 合体要求を登録する。登録できた場合のみ true。
   * merge の場合は即座に isMerging を立てて二重処理を防ぐ。
   */
  request(a: MergeCandidate, b: MergeCandidate): MergeDecision {
    const decision = evaluateMerge(a, b, this.maxLevel);
    const timestamp = this.now();

    if (decision.type === 'merge') {
      a.isMerging = true;
      b.isMerging = true;
      this.queue.push({
        a,
        b,
        nextLevel: decision.nextLevel!,
        type: 'merge',
        x: (a.x + b.x) / 2,
        y: (a.y + b.y) / 2,
        requestedAt: timestamp,
      });
      return decision;
    }

    if (decision.type === 'max-level-bonus') {
      const key = pairKey(a.id, b.id);
      const last = this.bonusCooldown.get(key) ?? -Infinity;
      if (timestamp - last < this.bonusCooldownMs) {
        return { type: 'reject', reason: 'bonus-cooldown' };
      }
      this.bonusCooldown.set(key, timestamp);
      this.queue.push({
        a,
        b,
        nextLevel: this.maxLevel,
        type: 'max-level-bonus',
        x: (a.x + b.x) / 2,
        y: (a.y + b.y) / 2,
        requestedAt: timestamp,
      });
      return decision;
    }

    return decision;
  }

  /** キューを取り出す（最大 limit 件）。取り出した分はキューから消える。 */
  drain(limit = Number.POSITIVE_INFINITY): MergeRequest[] {
    if (this.queue.length === 0) return [];
    const count = Math.min(this.queue.length, limit);
    return this.queue.splice(0, count);
  }

  /** 要求を取り消し、isMerging を戻す（合体できなくなった場合の保険）。 */
  cancel(request: MergeRequest): void {
    request.a.isMerging = false;
    request.b.isMerging = false;
  }

  /** キューを空にする（リスタート時）。 */
  clear(): void {
    this.queue.forEach((request) => this.cancel(request));
    this.queue.length = 0;
    this.bonusCooldown.clear();
  }

  /** 破棄済みピースのクールダウン情報を掃除する。 */
  forgetPiece(pieceId: number): void {
    for (const key of [...this.bonusCooldown.keys()]) {
      if (key.startsWith(`${pieceId}:`) || key.endsWith(`:${pieceId}`)) {
        this.bonusCooldown.delete(key);
      }
    }
  }
}

function pairKey(a: number, b: number): string {
  return a < b ? `${a}:${b}` : `${b}:${a}`;
}
